<template>
  <div
    v-on-clickaway="onClickaway"
    class="dropdown"
    :class="containerClass"
  >
    <button
      ref="button"
      type="button"
      class="btn dropdown__button"
      :disabled="disabled"
      @click.prevent="toggle"
    >
      <slot name="caption">
        {{ caption }}
      </slot>
      <Arrow
        :direction="isOpened ? 'top' : 'bottom'"
        class="dropdown__icon"
        tag="span"
      />
    </button>

    <div
      v-show="isOpened"
      class="dropdown__container"
    >
      <slot />
      <div
        v-if="$slots.submit"
        class="dropdown__container-submit"
      >
        <slot name="submit" />
      </div>
    </div>
  </div>
</template>

<script>
import { Arrow } from '@i22-td-smarthome/component-library';
import Dismissable from '@/mixins/dismissable';

export default {
  name: 'Dropdown',
  components: {
    Arrow,
  },
  mixins: [Dismissable],
  props: {
    caption: {
      type: String,
      default: '',
    },
    opened: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['focus'],
  computed: {
    containerClass() {
      return {
        'dropdown--open': this.isOpened,
        'dropdown--disabled': this.disabled,
      };
    },
    isOpenable() {
      return !this.disabled;
    },
  },
  watch: {
    opened(isOpened) {
      this.isOpened = isOpened;
    },
    disabled(disabled, wasDisabled) {
      if (disabled !== wasDisabled && !disabled) {
        this.close();
      }
    },
  },
  mounted() {
    document.addEventListener('focusin', this.handleFocusChange);
  },
  beforeDestroy() {
    document.removeEventListener('focusin', this.handleFocusChange);
  },
  methods: {
    focus() {
      this.$refs.button.focus();
      this.$emit('focus');
    },
    onEsc() {
      if (this.isClosed) return;
      this.close();
      this.focus();
    },
    handleFocusChange(event) {
      if (this.isClosed) return;

      const dropdownEl = this.$el;

      if (!dropdownEl.contains(event.target)) {
        this.close();
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.dropdown {
  display: inline-block;
  margin-bottom: 12px;
  position: relative;
  overflow: visible;
  user-select: none;

  @include mq($mq-md) {
    margin-bottom: 0;
  }

  &:first-child {
    margin-left: 0;
  }

  &--multi {
    border: 0;
  }

  &__icon {
    position: absolute;
    right: 10px;
    top: 12px;
    width: 0.7em;
    height: 0.5em;
  }

  &__button {
    @include font-size(18px, 27px);
    padding: 3px 30px 4px 12px;
    position: relative;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;

    .dropdown--open & {
      background-color: var(--color-background-subtle);
    }
  }

  &__container {
    display: block;
    height: auto;
    left: 0;
    margin-top: 12px;
    margin-left: $grid-medium-gutter * 0.5;
    margin-right: $grid-medium-gutter * 0.5;
    position: absolute;
    right: 0;
    visibility: visible;
    z-index: 3;
    background-color: var(--color-bg);
    color: var(--color-font);
    border: 1px solid var(--color-stroke);
    border-radius: 4px;
  }

  &__container-submit {
    border-top: 1px solid var(--color-stroke);
    padding: $grid-base;

    :deep(.link-button) {
      width: 100%;
      padding-inline: 0;
    }
  }
}
</style>
